import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Save, Loader2, PlugZap, ShieldCheck, Lock } from 'lucide-react';
import {
  createDataConnection,
  testConnectionDraft,
  updateDataConnection,
} from '../../Services/DataService';
import {
  ConnectionTestResult,
  DataConnection,
  DataConnectionRequest,
  DataEngine,
  ConnectionEnvironment,
  AccessPermission,
  ENGINE_OPTIONS,
  ENVIRONMENT_OPTIONS,
  PERMISSION_OPTIONS,
  TEST_STATUS_STYLES,
} from '../../Types/DataServiceType';

interface Props {
  isOpen: boolean;
  connection: DataConnection | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}

interface FormState {
  alias: string;
  databaseName: string;
  engine: DataEngine;
  host: string;
  port: string;
  username: string;
  password: string;
  schemaName: string;
  environment: ConnectionEnvironment;
  defaultPermission: AccessPermission;
  allowConsole: boolean;
  useSsl: boolean;
  trustCertificate: boolean;
  isShared: boolean;
  connectTimeoutMs: string;
  queryTimeoutSec: string;
  rowCap: string;
}

const emptyForm: FormState = {
  alias: '',
  databaseName: '',
  engine: 'MYSQL',
  host: '',
  port: '3306',
  username: '',
  password: '',
  schemaName: '',
  environment: 'DEVELOPMENT',
  defaultPermission: 'READ',
  allowConsole: false,
  useSsl: false,
  trustCertificate: false,
  isShared: false,
  connectTimeoutMs: '5000',
  queryTimeoutSec: '30',
  rowCap: '1000',
};

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400';

const toggleClass = (on: boolean) =>
  `flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-xs font-bold transition ${
    on
      ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
      : 'border-slate-800 bg-slate-950 text-slate-400 hover:border-slate-700'
  }`;

const ConnectionFormModal = ({ isOpen, connection, onClose, onSaved }: Props) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [formError, setFormError] = useState('');
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);

  const isEdit = Boolean(connection);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!isOpen) return;
    setFormError('');
    setTestResult(null);
    setSaving(false);

    if (connection) {
      setForm({
        alias: connection.alias ?? '',
        databaseName: connection.databaseName ?? '',
        engine: connection.engine ?? 'MYSQL',
        host: connection.host ?? '',
        port: String(connection.port ?? 3306),
        username: connection.username ?? '',
        // Never prefill the stored secret; a blank means "leave unchanged".
        password: '',
        schemaName: connection.schemaName ?? '',
        environment: connection.environment ?? 'DEVELOPMENT',
        defaultPermission: connection.defaultPermission ?? 'READ',
        allowConsole: Boolean(connection.allowConsole),
        useSsl: Boolean(connection.useSsl),
        trustCertificate: Boolean(connection.trustCertificate),
        isShared: Boolean(connection.isShared),
        connectTimeoutMs: String(connection.connectTimeoutMs ?? 5000),
        queryTimeoutSec: String(connection.queryTimeoutSec ?? 30),
        rowCap: String(connection.rowCap ?? 1000),
      });
    } else {
      setForm(emptyForm);
    }
  }, [isOpen, connection]);

  const validate = (): string => {
    if (!form.alias.trim()) return 'An alias is required.';
    if (!form.databaseName.trim()) return 'A database name is required.';
    if (!form.host.trim()) return 'A host is required.';
    if (!form.username.trim()) return 'A username is required.';
    if (!isEdit && !form.password) return 'A password is required for a new connection.';

    const port = Number(form.port);
    if (!Number.isInteger(port) || port < 1 || port > 65535) return 'The port must be between 1 and 65535.';

    const connectTimeout = Number(form.connectTimeoutMs);
    if (!Number.isInteger(connectTimeout) || connectTimeout < 1000 || connectTimeout > 60000) {
      return 'The connect timeout must be between 1000 and 60000 ms.';
    }

    const queryTimeout = Number(form.queryTimeoutSec);
    if (!Number.isInteger(queryTimeout) || queryTimeout < 1 || queryTimeout > 600) {
      return 'The query timeout must be between 1 and 600 seconds.';
    }

    const rowCap = Number(form.rowCap);
    if (!Number.isInteger(rowCap) || rowCap < 1 || rowCap > 50000) {
      return 'The row cap must be between 1 and 50000.';
    }

    return '';
  };

  const buildPayload = (): DataConnectionRequest => {
    const payload: DataConnectionRequest = {
      alias: form.alias.trim(),
      databaseName: form.databaseName.trim(),
      engine: form.engine,
      host: form.host.trim(),
      port: Number(form.port),
      username: form.username.trim(),
      environment: form.environment,
      defaultPermission: form.defaultPermission,
      allowConsole: form.allowConsole,
      useSsl: form.useSsl,
      trustCertificate: form.trustCertificate,
      isShared: form.isShared,
      connectTimeoutMs: Number(form.connectTimeoutMs),
      queryTimeoutSec: Number(form.queryTimeoutSec),
      rowCap: Number(form.rowCap),
    };

    if (form.schemaName.trim()) payload.schemaName = form.schemaName.trim();
    if (form.password) payload.password = form.password;

    return payload;
  };

  const handleTest = async () => {
    if (!form.password) {
      setFormError('Enter the password so the server can attempt a real connection.');
      return;
    }

    setTesting(true);
    setTestResult(null);
    setFormError('');

    try {
      const response = await testConnectionDraft({
        engine: form.engine,
        host: form.host.trim(),
        port: Number(form.port),
        databaseName: form.databaseName.trim(),
        schemaName: form.schemaName.trim() || undefined,
        username: form.username.trim(),
        password: form.password,
        useSsl: form.useSsl,
        trustCertificate: form.trustCertificate,
        connectTimeoutMs: Number(form.connectTimeoutMs),
      });

      if (response.success && response.data) {
        setTestResult(response.data);
      } else {
        setFormError(response.message || 'The connection test could not be run.');
      }
    } catch (err) {
      console.error('Connection test failed', err);
      setFormError('The connection test could not be run.');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async () => {
    const problem = validate();
    if (problem) {
      setFormError(problem);
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      const payload = buildPayload();
      const response = isEdit && connection
        ? await updateDataConnection(connection.id, payload)
        : await createDataConnection(payload);

      if (response.success) {
        onSaved(isEdit ? 'Connection updated.' : 'Connection created.');
        onClose();
      } else {
        setFormError(response.message || 'The connection could not be saved.');
      }
    } catch (err) {
      console.error('Failed to save connection', err);
      setFormError('The connection could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
            className="my-8 w-full max-w-3xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                  <PlugZap size={18} />
                </span>
                <div>
                  <h2 className="text-base font-black tracking-tight text-slate-100">
                    {isEdit ? 'Edit connection' : 'New connection'}
                  </h2>
                  <p className="text-[11px] font-semibold text-slate-500">
                    Passwords are encrypted at rest and are never returned by the API
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-5 px-6 py-5">
              {formError && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs font-bold text-rose-300">
                  {formError}
                </div>
              )}

              {testResult && (
                <div
                  className={`rounded-xl border px-4 py-3 ${
                    testResult.status === 'OK'
                      ? 'border-emerald-500/30 bg-emerald-500/10'
                      : 'border-amber-500/30 bg-amber-500/10'
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                        TEST_STATUS_STYLES[testResult.status] ?? 'border-slate-700 bg-slate-950 text-slate-400'
                      }`}
                    >
                      {testResult.status}
                    </span>
                    <p className="text-xs font-semibold text-slate-300">{testResult.message}</p>
                  </div>
                  {(testResult.serverVersion || testResult.latencyMs !== undefined) && (
                    <p className="mt-2 text-[11px] font-bold text-slate-500">
                      {testResult.serverVersion || 'Server'}
                      {testResult.latencyMs !== undefined ? ` · ${testResult.latencyMs} ms` : ''}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="md:col-span-2">
                  <label className={labelClass}>Alias *</label>
                  <input
                    type="text"
                    value={form.alias}
                    onChange={(e) => set('alias', e.target.value)}
                    placeholder="Analytics Read Replica"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Environment *</label>
                  <select
                    value={form.environment}
                    onChange={(e) => set('environment', e.target.value as ConnectionEnvironment)}
                    className={fieldClass}
                  >
                    {ENVIRONMENT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className={labelClass}>Engine *</label>
                  <select
                    value={form.engine}
                    onChange={(e) => set('engine', e.target.value as DataEngine)}
                    className={fieldClass}
                  >
                    {ENGINE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Database *</label>
                  <input
                    type="text"
                    value={form.databaseName}
                    onChange={(e) => set('databaseName', e.target.value)}
                    placeholder="reporting"
                    className={fieldClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="md:col-span-2">
                  <label className={labelClass}>Host *</label>
                  <input
                    type="text"
                    value={form.host}
                    onChange={(e) => set('host', e.target.value)}
                    placeholder="db.internal.example.com"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Port *</label>
                  <input
                    type="number"
                    value={form.port}
                    onChange={(e) => set('port', e.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Schema</label>
                  <input
                    type="text"
                    value={form.schemaName}
                    onChange={(e) => set('schemaName', e.target.value)}
                    placeholder="optional"
                    className={fieldClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className={labelClass}>Username *</label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={(e) => set('username', e.target.value)}
                    autoComplete="off"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <Lock size={11} className="mr-1 inline" />
                    Password {isEdit ? '' : '*'}
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => set('password', e.target.value)}
                    placeholder={isEdit ? 'Leave blank to keep the current password' : ''}
                    autoComplete="new-password"
                    className={fieldClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Default permission *</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {PERMISSION_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => set('defaultPermission', option.value)}
                      className={toggleClass(form.defaultPermission === option.value)}
                    >
                      <ShieldCheck size={14} />
                      <span className="text-left">
                        <span className="block">{option.label}</span>
                        <span className="mt-0.5 block text-[10px] font-semibold normal-case tracking-normal text-slate-500">
                          {option.value === 'READ'
                            ? 'Delegates can only read. Writes are refused.'
                            : 'Grants may be read or read/write, up to this ceiling.'}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className={labelClass}>Connect timeout (ms)</label>
                  <input
                    type="number"
                    value={form.connectTimeoutMs}
                    onChange={(e) => set('connectTimeoutMs', e.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Query timeout (s)</label>
                  <input
                    type="number"
                    value={form.queryTimeoutSec}
                    onChange={(e) => set('queryTimeoutSec', e.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Row cap</label>
                  <input
                    type="number"
                    value={form.rowCap}
                    onChange={(e) => set('rowCap', e.target.value)}
                    className={fieldClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Options</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => set('allowConsole', !form.allowConsole)}
                    className={toggleClass(form.allowConsole)}
                  >
                    <span
                      className={`h-4 w-7 rounded-full p-0.5 transition ${
                        form.allowConsole ? 'bg-cyan-500' : 'bg-slate-700'
                      }`}
                    >
                      <span className="block h-3 w-3 rounded-full bg-white transition" />
                    </span>
                    Allow SQL console
                  </button>
                  <button
                    type="button"
                    onClick={() => set('useSsl', !form.useSsl)}
                    className={toggleClass(form.useSsl)}
                  >
                    <span
                      className={`h-4 w-7 rounded-full p-0.5 transition ${
                        form.useSsl ? 'bg-cyan-500' : 'bg-slate-700'
                      }`}
                    >
                      <span className="block h-3 w-3 rounded-full bg-white transition" />
                    </span>
                    Use TLS
                  </button>
                  <button
                    type="button"
                    onClick={() => set('trustCertificate', !form.trustCertificate)}
                    className={toggleClass(form.trustCertificate)}
                  >
                    <span
                      className={`h-4 w-7 rounded-full p-0.5 transition ${
                        form.trustCertificate ? 'bg-cyan-500' : 'bg-slate-700'
                      }`}
                    >
                      <span className="block h-3 w-3 rounded-full bg-white transition" />
                    </span>
                    Trust self-signed certificate
                  </button>
                  <button
                    type="button"
                    onClick={() => set('isShared', !form.isShared)}
                    className={toggleClass(form.isShared)}
                  >
                    <span
                      className={`h-4 w-7 rounded-full p-0.5 transition ${
                        form.isShared ? 'bg-cyan-500' : 'bg-slate-700'
                      }`}
                    >
                      <span className="block h-3 w-3 rounded-full bg-white transition" />
                    </span>
                    Offer to delegates
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-800 px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleTest}
                disabled={testing || saving}
                className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
              >
                {testing ? <Loader2 size={15} className="animate-spin" /> : <PlugZap size={15} />}
                {testing ? 'Testing' : 'Test'}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                {saving ? 'Saving' : isEdit ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConnectionFormModal;
